import { Button, TextField, useAlert } from '@ads/components-react';
import { zodResolver } from '@hookform/resolvers/zod';
import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { StateViewModel } from '@/shared/api/generated';

import { useSaveState } from '../../../hooks/useSaveState';
import { type StateFormValues, stateFormSchema } from '../../../types/stateForm';
import { CountryAutocomplete } from '../CountryAutocomplete';

export interface StateFormModalProps {
  readonly open: boolean;
  /** Estado em edição; `null` abre o modal em modo de criação. */
  readonly state: StateViewModel | null;
  readonly onOpenChange: (open: boolean) => void;
}

const DEFAULT_VALUES: StateFormValues = { code: '', name: '', abbreviation: '', countryId: 0 };

/**
 * Modal de criação/edição de Estado (Radix Dialog + React Hook Form + Zod).
 *
 * Um único formulário cobre criar (campos vazios) e editar (pré-preenchido a
 * partir da linha, incluindo o País via `countryId`/`countryName`). O País é
 * escolhido num `SelectInput` alimentado por `useCountryDropdown`. Persiste via
 * `useSaveState` e mapeia o conflito **409** (código/sigla já cadastrados) para
 * erro nos campos, mantendo o modal aberto. Fiel aos frames *Estados — Modal:
 * Adicionar/Editar* de `design/location.pen`.
 */
export function StateFormModal({ open, state, onOpenChange }: StateFormModalProps) {
  const { t } = useTranslation('location');
  const { alert } = useAlert();
  const { save, isPending } = useSaveState();
  const [hasConflict, setHasConflict] = useState(false);

  const isEdit = state?.id != null;

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<StateFormValues>({
    resolver: zodResolver(stateFormSchema),
    defaultValues: DEFAULT_VALUES,
  });

  // Sincroniza o formulário sempre que o modal abre (com o estado em edição ou vazio).
  useEffect(() => {
    if (!open) return;
    setHasConflict(false);
    reset(
      state
        ? {
            code: state.code ?? '',
            name: state.name ?? '',
            abbreviation: state.abbreviation ?? '',
            countryId: state.countryId ?? 0,
          }
        : DEFAULT_VALUES,
    );
  }, [open, state, reset]);

  const onSubmit = handleSubmit(values => {
    setHasConflict(false);
    save(values, state?.id ?? undefined, {
      onSuccess: () => {
        alert({
          alertType: 'success',
          title: t(isEdit ? 'states.toast.updateSuccess' : 'states.toast.createSuccess'),
        });
        onOpenChange(false);
      },
      onConflict: () => setHasConflict(true),
      onError: () => alert({ alertType: 'warning', title: t('states.toast.saveError') }),
    });
  });

  const codeHelper = errors.code
    ? t('states.form.errors.codeRequired')
    : hasConflict
      ? t('states.form.errors.conflict')
      : undefined;
  const abbreviationHelper = errors.abbreviation
    ? t('states.form.errors.abbreviationRequired')
    : hasConflict
      ? t('states.form.errors.conflict')
      : undefined;

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <Dialog.Portal>
        {/* Backdrop manual: com `modal={false}` o Radix não renderiza o Dialog.Overlay.
            `modal={false}` é necessário para o dropdown do Autocomplete de País (portado
            ao body) permanecer clicável — no modo modal o Radix bloqueia os pointer-events
            fora do Content. Ver reference_ads_autocomplete_selecteditem. */}
        <div
          aria-hidden="true"
          className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft"
        />
        <Dialog.Content
          aria-describedby={undefined}
          // Não fechar por interação externa: com `modal={false}`, fechar o dropdown do
          // Autocomplete (portado ao body) dispararia o dismiss. Fecha só por X/Cancelar/Esc.
          onInteractOutside={e => e.preventDefault()}
          className="-translate-x-1/2 -translate-y-1/2 fixed top-1/2 left-1/2 z-50 flex w-full max-w-[36rem] flex-col gap-xxs rounded-lg bg-ctx-layout-surface p-xxs shadow-level4 focus:outline-none"
        >
          <Dialog.Title className="text-ctx-content-title ts-heading-xs">
            {t(isEdit ? 'states.form.editTitle' : 'states.form.createTitle')}
          </Dialog.Title>

          <form className="flex flex-col gap-mini" onSubmit={onSubmit} noValidate>
            <div className="grid grid-cols-1 gap-mini sm:grid-cols-[150px_1fr]">
              <Controller
                control={control}
                name="code"
                render={({ field }) => (
                  <TextField
                    label={`${t('states.form.code')}*`}
                    placeholder={t('states.form.codePlaceholder')}
                    value={field.value}
                    onChange={e => {
                      setHasConflict(false);
                      field.onChange(e.target.value);
                    }}
                    size="md"
                    fullWidth
                    hasError={Boolean(errors.code) || hasConflict}
                    {...(codeHelper ? { helper: codeHelper } : {})}
                  />
                )}
              />

              <Controller
                control={control}
                name="name"
                render={({ field }) => (
                  <TextField
                    label={`${t('states.form.name')}*`}
                    placeholder={t('states.form.namePlaceholder')}
                    value={field.value}
                    onChange={e => field.onChange(e.target.value)}
                    size="md"
                    fullWidth
                    hasError={Boolean(errors.name)}
                    {...(errors.name ? { helper: t('states.form.errors.nameRequired') } : {})}
                  />
                )}
              />
            </div>

            <div className="grid grid-cols-1 gap-mini sm:grid-cols-[150px_1fr]">
              <Controller
                control={control}
                name="abbreviation"
                render={({ field }) => (
                  <TextField
                    label={`${t('states.form.abbreviation')}*`}
                    placeholder={t('states.form.abbreviationPlaceholder')}
                    value={field.value}
                    onChange={e => {
                      setHasConflict(false);
                      field.onChange(e.target.value);
                    }}
                    size="md"
                    fullWidth
                    hasError={Boolean(errors.abbreviation) || hasConflict}
                    {...(abbreviationHelper ? { helper: abbreviationHelper } : {})}
                  />
                )}
              />

              <Controller
                control={control}
                name="countryId"
                render={({ field }) => (
                  <div className="flex flex-col gap-quark">
                    <CountryAutocomplete
                      label={`${t('states.form.country')}*`}
                      placeholder={t('states.form.countryPlaceholder')}
                      value={field.value}
                      selectedLabel={state?.countryName ?? ''}
                      onChange={id => field.onChange(id)}
                      hasError={Boolean(errors.countryId)}
                    />
                    {errors.countryId ? (
                      <p className="text-base-danger-600 ts-caption-xxs">
                        {t('states.form.errors.countryRequired')}
                      </p>
                    ) : null}
                  </div>
                )}
              />
            </div>

            <div className="mt-nano flex justify-end gap-nano">
              <Dialog.Close asChild>
                <Button hierarchy="secondary" size="md" type="button">
                  {t('states.form.cancel')}
                </Button>
              </Dialog.Close>
              <Button
                hierarchy="primary"
                size="md"
                type="submit"
                isLoading={isPending}
                disabled={isPending}
              >
                {t('states.form.save')}
              </Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
